import PageTitle from "components/pageTitle";
import DataTable, { Column } from "components/DataTable";
import { StatusChip } from "components/StatusChip";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { conductLegalResearch } from "types";
import { useAuth } from "contexts/AuthContext";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import {
  getCommitteeLegalResearchRequests,
  getCommitteeLegalResearchRequestsByStakeholder,
  getInternalLegalResearchRequests,
  getInternalLegalResearchRequestsByProsecutor,
  getLegalResearchRequests,
  getLegalResearchRequestsByStakeHolder,
} from "services/legalResearchService";
import { LoadingPageIcon } from "utils/constant";
import { useToast } from "contexts/ToastContext";
import {
  getEmployeesByAdminAndOrg,
  getStakeholder,
} from "services/getEmployees";
import { calculateRemainingDays } from "utils/helpers";
import { getTokenPayloadSafely } from "App";

const ConductLegalResearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const contextData = useAuth();
  const { showToast } = useToast();

  // Get active tab from location state or pathname or default to "New Request"
  const getActiveTabFromState = () => {
    if (location.state?.activeTab) return location.state.activeTab;

    if (location.pathname.includes('/external')) return t("ExternalRequest");
    if (location.pathname.includes('/internal')) return t("InternalRequestTopics");
    if (location.pathname.includes('/committee')) return t("CommitteeRequest");
    if (location.pathname.includes('/archived')) return t("Archived");
    if (location.pathname.includes('/legal-research')) return t("LegalResearchRequest");
    if (location.pathname.includes('/scheduled')) return t("ScheduledPresentation");
    if (location.pathname.includes('/proposal-submission')) return t("ProposalSubmission");

    return t("NewRequest");
  };

  const [activeTab, setActiveTab] = useState<string>(getActiveTabFromState());
  const [data, setData] = useState<{
    isLoading: boolean;
    data: conductLegalResearch[];
  }>({ isLoading: true, data: [] });
  const [internalData, setInternalData] = useState<any>({});
  const [prosecutorInternalData, setProsecutorInternalData] = useState<{
    isLoading: boolean;
    data: any[];
  }>({ isLoading: true, data: [] });
  const [committeeData, setCommitteeData] = useState<any>({});
  const [filteredData, setFilteredData] = useState<{
    isLoading: boolean;
    data: conductLegalResearch[];
  }>({ isLoading: true, data: [] });
  const [tabFilteredData, setTabFilteredData] = useState<{
    isLoading: boolean;
    data: conductLegalResearch[];
  }>({ isLoading: true, data: [] });

  // Update active tab when location state changes
  useEffect(() => {
    setActiveTab(getActiveTabFromState());
  }, [location.state]);

  // Function to handle tab change
  const handleTabChange = (tabName: string) => {
    navigate("/conduct-legal-research", {
      state: { activeTab: tabName },
      replace: true,
    });
  };

  // Handle view details navigation with tab state preservation
  const handleViewDetails = (row: any) => {
    const detailPath =
      activeTab === t("InternalRequestTopics")
        ? `internal/${row?.id}`
        : activeTab === t("CommitteeRequest")
        ? `committee/${row?.id}`
        : `${row?.id}`;

    navigate(detailPath, {
      state: { activeTab: activeTab },
    });
  };

  // Handle create request navigation with tab state preservation
  const handleCreateRequest = (path: string) => {
    navigate(path, {
      state: { activeTab: activeTab },
    });
  };

  // Check if current tab is Internal Request or Internal Request Topics
  const isInternalRequestTab = activeTab === t("InternalRequest") || activeTab === t("InternalRequestTopics");

  const columns: Column<conductLegalResearch>[] = [
    {
      header: isInternalRequestTab ? t("InternalResearchTask") : t("Name"),
      accessor: "accused_prosecutor_name",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex relative">
            <img
              src={require("../../images/reasearch_avatar.png")}
              className="w-10 h-10 rounded-full grayscale"
              alt={t("ResearchAvatarAlt")}
            />
            <svg
              className="absolute left-6 top-7 rounded-full"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              {/* SVG content remains unchanged */}
              <g filter="url(#filter0_d_9_31077)">
                <path
                  d="M18.1163 6.42625C17.8806 6.18 17.6369 5.92625 17.545 5.70312C17.46 5.49875 17.455 5.16 17.45 4.83187C17.4406 4.22187 17.4306 3.53062 16.95 3.05C16.4694 2.56937 15.7781 2.55937 15.1681 2.55C14.84 2.545 14.5013 2.54 14.2969 2.455C14.0744 2.36312 13.82 2.11937 13.5737 1.88375C13.1425 1.46937 12.6525 1 12 1C11.3475 1 10.8581 1.46937 10.4262 1.88375C10.18 2.11937 9.92625 2.36312 9.70312 2.455C9.5 2.54 9.16 2.545 8.83187 2.55C8.22187 2.55937 7.53062 2.56937 7.05 3.05C6.56937 3.53062 6.5625 4.22187 6.55 4.83187C6.545 5.16 6.54 5.49875 6.455 5.70312C6.36312 5.92562 6.11937 6.18 5.88375 6.42625C5.46937 6.8575 5 7.3475 5 8C5 8.6525 5.46937 9.14187 5.88375 9.57375C6.11937 9.82 6.36312 10.0737 6.455 10.2969C6.54 10.5013 6.545 10.84 6.55 11.1681C6.55937 11.7781 6.56937 12.4694 7.05 12.95C7.53062 13.4306 8.22187 13.4406 8.83187 13.45C9.16 13.455 9.49875 13.46 9.70312 13.545C9.92562 13.6369 10.18 13.8806 10.4262 14.1163C10.8575 14.5306 11.3475 15 12 15C12.6525 15 13.1419 14.5306 13.5737 14.1163C13.82 13.8806 14.0737 13.6369 14.2969 13.545C14.5013 13.46 14.84 13.455 15.1681 13.45C15.7781 13.4406 16.4694 13.4306 16.95 12.95C17.4306 12.4694 17.4406 11.7781 17.45 11.1681C17.455 10.84 17.46 10.5013 17.545 10.2969C17.6369 10.0744 17.8806 9.82 18.1163 9.57375C18.5306 9.1425 19 8.6525 19 8C19 7.3475 18.5306 6.85812 18.1163 6.42625ZM14.8538 6.85375L11.3538 10.3538C11.3073 10.4002 11.2522 10.4371 11.1915 10.4623C11.1308 10.4874 11.0657 10.5004 11 10.5004C10.9343 10.5004 10.8692 10.4874 10.8085 10.4623C10.7478 10.4371 10.6927 10.4002 10.6462 10.3538L9.14625 8.85375C9.09979 8.8073 9.06294 8.75214 9.0378 8.69145C9.01266 8.63075 8.99972 8.5657 8.99972 8.5C8.99972 8.4343 9.01266 8.36925 9.0378 8.30855C9.06294 8.24786 9.09979 8.1927 9.14625 8.14625C9.24007 8.05243 9.36732 7.99972 9.5 7.99972C9.5657 7.99972 9.63075 8.01266 9.69145 8.0378C9.75214 8.06294 9.8073 8.09979 9.85375 8.14625L11 9.29313L14.1462 6.14625C14.1927 6.09979 14.2479 6.06294 14.3086 6.0378C14.3692 6.01266 14.4343 5.99972 14.5 5.99972C14.5657 5.99972 14.6308 6.01266 14.6914 6.0378C14.7521 6.06294 14.8073 6.09979 14.8538 6.14625C14.9002 6.1927 14.9371 6.24786 14.9622 6.30855C14.9873 6.36925 15.0003 6.4343 15.0003 6.5C15.0003 6.5657 14.9873 6.63075 14.9622 6.69145C14.9371 6.75214 14.9002 6.8073 14.8538 6.85375Z"
                  fill="#039D53"
                />
                <path
                  d="M12 0.5C12.8873 0.5 13.5213 1.14032 13.9189 1.52246C14.045 1.64307 14.1581 1.75002 14.2646 1.83887C14.3753 1.9311 14.4473 1.97658 14.4873 1.99316H14.4893C14.5174 2.00477 14.5896 2.02278 14.7275 2.03418C14.8588 2.04501 15.0078 2.04724 15.1758 2.0498L15.6553 2.06348C16.1813 2.09465 16.8178 2.21039 17.3037 2.69629C17.9515 3.34408 17.9415 4.25941 17.9502 4.82422L17.9658 5.27246C17.9716 5.34182 17.979 5.39476 17.9863 5.43359L18.0068 5.51074L18.0078 5.5127C18.0243 5.5527 18.0689 5.62481 18.1611 5.73535C18.2498 5.84163 18.356 5.9541 18.4766 6.08008C18.8582 6.47778 19.5 7.11199 19.5 8C19.5 8.88724 18.8587 9.52032 18.4766 9.91797L18.4775 9.91895C18.3569 10.045 18.25 10.1581 18.1611 10.2646C18.0689 10.3753 18.0234 10.4473 18.0068 10.4873V10.4893C17.9952 10.5174 17.9772 10.5896 17.9658 10.7275L17.9502 11.1758C17.9415 11.7406 17.9515 12.6559 17.3037 13.3037C16.8178 13.7896 16.1813 13.9053 15.6553 13.9365L15.1758 13.9502C15.0078 13.9528 14.8588 13.955 14.7275 13.9658C14.5896 13.9772 14.5174 13.9952 14.4893 14.0068L14.4873 14.0078C14.4473 14.0243 14.3752 14.0689 14.2646 14.1611C14.1582 14.25 14.0452 14.3567 13.9189 14.4775C13.5212 14.8592 12.8874 15.5 12 15.5C11.1127 15.5 10.4787 14.8597 10.0811 14.4775L9.90039 14.3076C9.84324 14.2546 9.78862 14.2055 9.73535 14.1611C9.62473 14.0689 9.55271 14.0234 9.5127 14.0068H9.51074C9.48265 13.9952 9.41044 13.9772 9.27246 13.9658L8.82422 13.9502C8.25941 13.9415 7.34408 13.9515 6.69629 13.3037C6.21039 12.8178 6.09465 12.1813 6.06348 11.6553L6.0498 11.1758C6.04724 11.0078 6.04501 10.8588 6.03418 10.7275C6.02278 10.5896 6.00477 10.5174 5.99316 10.4893L5.99219 10.4873C5.97568 10.4473 5.93109 10.3752 5.83887 10.2646C5.75002 10.1582 5.64329 10.0452 5.52246 9.91895C5.14079 9.5212 4.5 8.88745 4.5 8C4.5 7.11201 5.14141 6.47766 5.52344 6.08008C5.64376 5.95433 5.75019 5.8417 5.83887 5.73535C5.9311 5.62473 5.97658 5.55271 5.99316 5.5127V5.51074C6.00477 5.48265 6.02278 5.41044 6.03418 5.27246C6.04501 5.14121 6.04724 4.99221 6.0498 4.82422V4.82129C6.06127 4.26139 6.04857 3.34401 6.69629 2.69629C7.34408 2.04849 8.25941 2.05849 8.82422 2.0498L9.27344 2.03418C9.41149 2.02277 9.48286 2.00527 9.50977 1.99414L9.5127 1.99219C9.5527 1.97568 9.62481 1.93109 9.73535 1.83887C9.84184 1.75002 9.95478 1.64329 10.0811 1.52246C10.4788 1.14079 11.1126 0.5 12 0.5Z"
                  stroke="white"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_9_31077"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_9_31077"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_9_31077"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </div>
          {row?.requested_by === "internal" ? (
            <div className="">
              <p className="text-sm"> {row?.research_task_id} </p>
            </div>
          ) : (
            <span>
              <p className="text-sm">{row?.organization_name}</p>
              <p className="text-xs">{row?.letter_reference_number}</p>
            </span>
          )}
        </div>
      ),
      widthClass: "w-[25%]",
      searchValue: (item) => String(item?.requested_by_user?.full_name || ""),
    },
    {
      header: isInternalRequestTab ? t("CreatedDate") : t("ApplicationNumber"),
      accessor: isInternalRequestTab ? "created_at" : "application_number",
      render: (row) => <p>{isInternalRequestTab ? (row?.created_at ? new Date(row.created_at).toLocaleDateString() : "-") : (row?.application_number || "-")}</p>,
      searchValue: (item) => String(item?.application_number || ""),
      widthClass: "w-[15%]",
    },
    {
      header: t("Status"),
      accessor: "status",
      render: (row) => {
        const statusObj = Object.values(LEGAL_RESEARCH_REQUEST_STATUS).find(
          (s) => s.value == row?.status
        );
        return <StatusChip status={statusObj?.label || row?.status || ""} />;
      },
      widthClass: "w-[15%]",
    },
    {
      header: t("ForwardStatus"),
      accessor: "forward_status",
      render: (row) => {
        const statusObj = Object.values(LEGAL_RESEARCH_REQUEST_STATUS).find(
          (s) => s.value == row?.forward_status
        );
        return (
          <StatusChip status={statusObj?.label || row?.forward_status || ""} />
        );
      },
      widthClass: "w-[15%]",
    },
    ...(data.data.some(
      (row) => row?.status === LEGAL_RESEARCH_REQUEST_STATUS.completed.value
    )
      ? [
          {
            header: t("RegistrationNumber"),
            accessor: "registry_reference_number",
            render: (row: any) => {
              return (
                <p className="pl-3">
                  {row?.registry_reference_number
                    ? row.registry_reference_number
                    : "--------"}
                </p>
              );
            },
            widthClass: "w-[15%]",
          },
        ]
      : []),
    {
      header: t("ViewAction"),
      accessor: "action",
      render: (row: any) => {
        const titleSchedule = row?.schedules?.find(
          (schedule: any) => schedule.purpose === "proposal_presentation"
        );
        
        const remainingDays = titleSchedule?.scheduled_datetime 
          ? calculateRemainingDays(titleSchedule.scheduled_datetime)
          : null;
        
        let badgeClass = "bg-green-500";
        const hasValidDays = remainingDays !== null && remainingDays !== undefined;

        if (hasValidDays) {
          if (remainingDays < 0) {
            badgeClass = "bg-red-500";
          } else if (remainingDays === 0) {
            badgeClass = "bg-orange-500";
          } else if (remainingDays <= 3) {
            badgeClass = "bg-yellow-500";
          } else {
            badgeClass = "bg-green-500";
          }
        }

        // Research (draft) presentation schedule badge
        const draftSchedule = row?.schedules?.find(
          (schedule: any) => schedule.purpose === "draft_presentation"
        );
        const draftDays = draftSchedule?.scheduled_datetime
          ? calculateRemainingDays(draftSchedule.scheduled_datetime)
          : null;
        const hasDraftDays = draftDays !== null && draftDays !== undefined;
        let draftBadgeClass = "bg-green-500";
        if (hasDraftDays) {
          if (draftDays < 0) draftBadgeClass = "bg-red-500";
          else if (draftDays === 0) draftBadgeClass = "bg-orange-500";
          else if (draftDays <= 3) draftBadgeClass = "bg-yellow-500";
        }

        return (
          <div className="flex gap-2 items-center cursor-pointer group" onClick={() => handleViewDetails(row)}>
            {row?.proposal_deadline &&
              !row?.proposal_summary &&
              (row?.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
                row?.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value) &&
              (() => {
                const deadlineDays = calculateRemainingDays(
                  row.proposal_deadline
                );
                let deadlineClass = "bg-green-500";
                if (deadlineDays < 0) deadlineClass = "bg-red-500";
                else if (deadlineDays === 0) deadlineClass = "bg-orange-500";
                else if (deadlineDays <= 3) deadlineClass = "bg-yellow-500";
                const deadlineLabel =
                  deadlineDays < 0
                    ? t("OverdueByDays", { count: Math.abs(deadlineDays) })
                    : deadlineDays === 0
                    ? t("Today")
                    : t("DaysLeft", { count: deadlineDays });
                return (
                  <div
                    className={`${deadlineClass} text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[24px] text-center`}
                    title={`${t("DeadlineForProposal")}: ${new Date(
                      row.proposal_deadline
                    ).toLocaleDateString()} — ${deadlineLabel}`}
                  >
                    {deadlineDays < 0 ? `-${Math.abs(deadlineDays)}` : deadlineDays}
                  </div>
                );
              })()}
            {hasValidDays && titleSchedule && !draftSchedule && (
              <div className="relative">
                <div className={`${badgeClass} text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[24px] text-center group-hover:scale-110 transition-transform`}>
                  {remainingDays < 0 ? Math.abs(remainingDays) : remainingDays}
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  <div className="font-semibold mb-1">{t("ProposalPresentation")}</div>
                  {row?.research_task && <div className="text-blue-300 mb-1">{row.research_task}</div>}
                  <div>📅 {new Date(titleSchedule.scheduled_datetime).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  {titleSchedule.location && <div>📍 {titleSchedule.location}</div>}
                  {titleSchedule.remark && <div className="text-gray-300 mt-1">{titleSchedule.remark}</div>}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            )}
            {hasDraftDays && draftSchedule && (
              <div className="relative">
                <div className={`${draftBadgeClass} text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[24px] text-center group-hover:scale-110 transition-transform`}>
                  {draftDays < 0 ? Math.abs(draftDays) : draftDays}
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  <div className="font-semibold mb-1">{t("ResearchDraftPresentation")}</div>
                  {row?.research_task && <div className="text-blue-300 mb-1">{row.research_task}</div>}
                  <div>📅 {new Date(draftSchedule.scheduled_datetime).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  {draftSchedule.location && <div>📍 {draftSchedule.location}</div>}
                  {draftSchedule.remark && <div className="text-gray-300 mt-1">{draftSchedule.remark}</div>}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="22"
              viewBox="0 0 17 22"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10 0V5.5C10 5.89782 10.158 6.27936 10.4393 6.56066C10.7206 6.84196 11.1022 7 11.5 7H17V14C17 14.5304 16.7893 15.0391 16.4142 15.4142C16.0391 15.7893 15.5304 16 15 16H13V18C13 18.5304 12.7893 19.0391 12.4142 19.4142C12.0391 19.7893 11.5304 20 11 20H2C1.46957 20 0.960859 19.7893 0.585786 19.4142C0.210714 19.0391 0 18.5304 0 18V6C0 5.46957 0.210714 4.96086 0.585786 4.58579C0.960859 4.21071 1.46957 4 2 4H4V2C4 1.46957 4.21071 0.960859 4.58579 0.585786C4.96086 0.210714 5.46957 0 6 0H10ZM4 6H2V18H11V16H6C5.46957 16 4.96086 15.7893 4.58579 15.4142C4.21071 15.0391 4 14.5304 4 14V6ZM12 0.043C12.3789 0.123355 12.7263 0.311992 13 0.586L16.414 4C16.688 4.27373 16.8766 4.62112 16.957 5H12V0.043Z"
                fill="#212121"
              />
            </svg>
            <svg
              className="my-auto"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <mask
                id="mask0_9_31087"
                maskUnits="userSpaceOnUse"
                x="1"
                y="5"
                width="22"
                height="14"
              >
                <path
                  d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="1.33333"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                  fill="black"
                  stroke="black"
                  strokeWidth="1.33333"
                  strokeLinejoin="round"
                />
              </mask>
              <g mask="url(#mask0_9_31087)">
                <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
              </g>
            </svg>
          </div>
        )},
      widthClass: "w-[10%]",
    },
  ];

  // Fetch committee data
  useEffect(() => {
    const fetchCommitteeData = async () => {
      try {
        let data: any;
        if (
          contextData?.user?.user_type === "external" &&
          contextData?.user?.org_unit_id
        ) {
          data = await getCommitteeLegalResearchRequestsByStakeholder(
            contextData.token,
            contextData.user.org_unit_id
          );
        } else {
          data = await getCommitteeLegalResearchRequests(contextData.token);
        }
        setCommitteeData(data);
        console.log("Committee legal research requests:", data);
      } catch (error) {
        console.error("Error fetching legal research requests:", error);
        showToast(t("FailedToFetchCommitteeResearchRequest"), "error");
      }
    };

    if (contextData.token) fetchCommitteeData();
  }, [
    contextData.token,
    contextData?.user?.user_type,
    contextData?.user?.org_unit_id,
  ]);

  // Fetch internal data
  useEffect(() => {
    if (!contextData.token) return;
    getInternalLegalResearchRequests(contextData.token)
      .then((data) => {
        setInternalData(data);
        console.log("Internal legal research requests:", data);
      })
      .catch((error) => {
        console.error("Error fetching legal research requests:", error);
        showToast(t("FailedToFetchInternalLegalResearchRequest"), "error");
      });
  }, [contextData.token]);

  // Fetch internal data by prosecutor for LegalResearchProsecutor role
  useEffect(() => {
    if (!contextData?.token) return;
    const isProsecutor = contextData?.user?.roles?.some(
      (p: string) => p === "LegalResearchProsecutor"
    );
    
    if (isProsecutor && contextData?.user?.user_id) {
      getInternalLegalResearchRequestsByProsecutor(
        contextData.token,
        contextData.user.user_id
      )
        .then((data) => {
          setProsecutorInternalData({ isLoading: false, data: data });
          console.log("Internal legal research requests by prosecutor:", data);
        })
        .catch((error) => {
          console.error("Error fetching internal requests by prosecutor:", error);
          setProsecutorInternalData({ isLoading: false, data: [] });
        });
    } else {
      setProsecutorInternalData({ isLoading: false, data: [] });
    }
  }, [contextData?.token, contextData?.user?.user_id, contextData?.user?.roles]);

  // Fetch main data
  useEffect(() => {
    const fetchData = async () => {
      try {
        let data: conductLegalResearch[];
        const bearer =
          contextData.token || contextData?.user?.access_token || "";
        if (!bearer) return;
        
        const decoded = getTokenPayloadSafely(bearer);
        
        if (
          contextData?.user?.user_type === "external" &&
          decoded?.organization?.uu_id
        ) {
          data = await getLegalResearchRequestsByStakeHolder(
            bearer,
            decoded.organization.uu_id
          );
        } else {
          data = await getLegalResearchRequests(bearer);
        }

        setData((prev) => {
          let filteredData = data;

          if (contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
             contextData?.user?.roles?.some((p) => p === "LRDIFUDeputy"))
            filteredData = data;
          else if (
            contextData?.user?.roles?.some((p) => p === "OAGBManagementHead")
          )
            filteredData = data?.filter(
              (req: any) =>
                req.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value
            );
          else if (
            contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")
          )
            filteredData = data;
          else if (
            contextData?.user?.roles?.some(
              (p) => p === "LegalResearchCoordinator"
            )
          )
            filteredData = data?.filter(
              (req: any) =>
                (req.requested_by === "external" &&
                  req.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value) ||
                (req.forward_status !==
                  LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
                  req.forward_status !==
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                  req.forward_status !==
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management
                      .value &&
                  req.forward_status !==
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director
                      .value) ||
                (req.requested_by === "internal" &&
                  req.requested_by_id === contextData?.user.user_id) ||
                req.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value ||
                (req.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
                  req.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value)
            );
          else if (
            contextData?.user?.roles?.some(
              (p) => p === "LegalResearchProsecutor"
            )
          )
            filteredData = data;
          else if (contextData?.user?.roles?.some((p) => p === "Registrar"))
            filteredData = data?.filter(
              (req: any) =>
                req.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.sent_to_registrar.value
            );
          return { ...prev, data: filteredData };
        });
      } catch (error) {
        console.error("Error fetching legal research requests:", error);
        showToast(t("FailedToFetchLegalResearchRequest"), "error");
      } finally {
        setData((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    fetchData();
  }, [contextData]);

  // Cache for user data to avoid duplicate API calls
  const userCache = new Map<string, any>();
  const [cacheKey] = useState(() => `user_cache_${Date.now()}`);

  // Enrich data with user information
  useEffect(() => {
    if (!data?.data || data.data.length === 0) {
      setFilteredData({ isLoading: false, data: [] });
      return;
    }

    const token = contextData.token || contextData?.user?.access_token || "";
    
    let allInternalUsers: any[] = [];
    const fetchAllInternalUsers = async () => {
      if (contextData.user.user_type === "internal") {
        try {
          const users = await getEmployeesByAdminAndOrg({
            bearer: token,
            org_unit: contextData?.user?.org_unit || "",
            admin_unit: contextData?.user?.admin_unit || "",
          });
          allInternalUsers = users.map((u: { roles: string[]; user: any }) => u.user);
        } catch (error) {
          console.error("Error fetching internal users:", error);
        }
      }
    };
    
    const fetchUserWithCache = async (research: conductLegalResearch): Promise<conductLegalResearch> => {
      if (!research.requested_by_id) return research;
      
      const cacheKey = `user_${research.requested_by_id}_${research.requested_by}`;
      
      if (userCache.has(cacheKey)) {
        return {
          ...research,
          requested_by_user: userCache.get(cacheKey),
        };
      }

      try {
        let user;
        if (research.requested_by === "external") {
          user = await getStakeholder(research.requested_by_id, token);
        } else if (contextData.user.user_type === "internal") {
          user = allInternalUsers.find(u => u.id === research.requested_by_id);
        }
        
        if (user) {
          userCache.set(cacheKey, user);
        }
        
        return {
          ...research,
          requested_by_user: user,
        };
      } catch (error) {
        console.error(error);
        return research;
      }
    };

    const processBatch = async () => {
      await fetchAllInternalUsers();
      
      const BATCH_SIZE = 5;
      const DELAY_MS = 50;
      const enrichedResults: conductLegalResearch[] = [];
      
      for (let i = 0; i < data.data.length; i += BATCH_SIZE) {
        const batch = data.data.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(fetchUserWithCache)
        );
        enrichedResults.push(...batchResults);
        
        if (i + BATCH_SIZE < data.data.length) {
          await new Promise(resolve => setTimeout(resolve, DELAY_MS));
        }
      }
      
      setFilteredData({
        isLoading: false,
        data: enrichedResults,
      });
    };

    processBatch();
  }, [data.data, contextData]);

  // Filter data based on active tab
  useEffect(() => {
    let tabData;
    
    if (activeTab === t("NewRequest")) {
      tabData = filteredData.data?.filter(
        (req: any) => req.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value && req.requested_by === "internal"
      );
    } else if (activeTab === t("InternalRequest")) {
      tabData = filteredData?.data?.filter(
        (req: any) => req.requested_by === "internal"
      );
    } else if (activeTab === t("InternalRequestTopics")) {
      const isProsecutor = contextData?.user?.roles?.some(
        (p: string) => p === "LegalResearchProsecutor"
      );
      if (isProsecutor && prosecutorInternalData.data.length > 0) {
        tabData = prosecutorInternalData.data;
      } else {
        tabData = internalData;
      }
    } else if (activeTab === t("ScheduledPresentation")) {
      tabData = filteredData?.data?.filter(
        (req: any) =>
          req.requested_by === "internal" &&
          (((req.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value ||
            req.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value) &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "proposal_presentation"
            )) ||
          (req.status === LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "title_presentation"
            )))
      );
    } else if (activeTab === t("InternalPublished")) {
      tabData = filteredData?.data?.filter(
        (req: any) => req.requested_by === "internal" && req.is_published
      );
    }
    else if (
      activeTab === t("ExternalRequest") ||
      activeTab === t("LegalResearchRequest")
    ) {
      tabData = filteredData?.data?.filter(
        (req: any) => req.requested_by === "external" || req.requested_by === ""
      );
    } else if (activeTab === t("ExternalNewRequest")) {
      tabData = filteredData.data?.filter(
        (req: any) => req.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (req.requested_by === "external" || req.requested_by === "")
      );
    } else if (activeTab === t("ExternalScheduledPresentation")) {
      tabData = filteredData?.data?.filter(
        (req: any) =>
          (req.requested_by === "external" || req.requested_by === "") &&
          (((req.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value ||
            req.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value) &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "proposal_presentation"
            )) ||
          (req.status === LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "title_presentation"
            )))
      );
    } else if (activeTab === t("ExternalPublished")) {
      tabData = filteredData?.data?.filter(
        (req: any) =>
          (req.requested_by === "external" || req.requested_by === "") &&
          req.is_published
      );
    }
    else if (activeTab === t("CommitteeRequest")) {
      tabData = committeeData;
    } else if (activeTab === t("CommitteeNewRequest")) {
      tabData = committeeData?.filter(
        (req: any) => req.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value
      );
    } else if (activeTab === t("CommitteeScheduledPresentation")) {
      tabData = committeeData?.filter(
        (req: any) =>
          ((req.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value ||
            req.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value) &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "proposal_presentation"
            )) ||
          (req.status === LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
            req.schedules?.find(
              (schedule: any) => schedule.purpose === "title_presentation"
            ))
      );
    } else if (activeTab === t("CommitteePublished")) {
      tabData = committeeData?.filter((req: any) => req.is_published);
    }

    setTabFilteredData({
      isLoading: false,
      data: tabData || [],
    });
  }, [activeTab, filteredData.data, internalData, committeeData, prosecutorInternalData]);

  // Check if Internal Request tab group is active
  const isInternalRequestGroupActive =
    activeTab === t("InternalRequest") ||
    activeTab === t("InternalRequestTopics") ||
    activeTab === t("NewRequest") ||
    activeTab === t("ScheduledPresentation") ||
    activeTab === t("InternalPublished");

  // Check if External Request tab group is active
  const isExternalRequestGroupActive =
    activeTab === t("ExternalRequest") ||
    activeTab === t("LegalResearchRequest") ||
    activeTab === t("ExternalNewRequest") ||
    activeTab === t("ExternalScheduledPresentation") ||
    activeTab === t("ExternalPublished");

  // Check if Committee Request tab group is active
  const isCommitteeRequestGroupActive =
    activeTab === t("CommitteeRequest") ||
    activeTab === t("CommitteeNewRequest") ||
    activeTab === t("CommitteeScheduledPresentation") ||
    activeTab === t("CommitteePublished");

  return (
    <div className="mx-auto flex flex-col space-y-4">
      <div className="w-full flex flex-wrap items-left justify-between gap-4 flex-col">
        <div className="flex justify-between items-center">
          {contextData.user.user_type == "external" ? (
            <PageTitle
              icon="⚖"
              text={t("RequestForConductingLegalResearch")}
              subtitle={t("LegalResearchSubtitle")}
              iconColor="text-purple-600"
              textColor="text-[#432101]"
            />
          ) : (
            <PageTitle
              icon="⚖"
              text={t("ConductResearchRequest")}
              iconColor="text-purple-600"
              textColor="text-[#432101]"
            />
          )}
        </div>
        <div className="flex justify-between items-center">
          <div className="flex flex-wrap items-center justify-start gap-2">
            {contextData.user.user_type !== "external" && (
              <button
                onClick={() => handleTabChange(t("InternalRequest"))}
                type="button"
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                  isInternalRequestGroupActive
                    ? "bg-black text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                }`}
              >
                {t("InternalRequest")}
              </button>
            )}

            <button
              onClick={() => handleTabChange(t("ExternalRequest"))}
              type="button"
              className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                isExternalRequestGroupActive
                  ? "bg-black text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              {t("ExternalRequest")}
            </button>

            <button
              onClick={() => handleTabChange(t("CommitteeRequest"))}
              type="button"
              className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                isCommitteeRequestGroupActive
                  ? "bg-black text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              {t("CommitteeRequest")}
            </button>

            {isInternalRequestGroupActive && contextData.user.user_type !== "external" && (
              <>
                <button
                  onClick={() => handleTabChange(t("InternalRequestTopics"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ml-4 ${
                    activeTab === t("InternalRequestTopics")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("InternalRequestTopics")}
                </button>

                <button
                  onClick={() => handleTabChange(t("NewRequest"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("NewRequest")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("NewRequest")}
                </button>

                <button
                  onClick={() => handleTabChange(t("ScheduledPresentation"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("ScheduledPresentation")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("ScheduledPresentation")}
                </button>

                <button
                  onClick={() => handleTabChange(t("InternalPublished"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("InternalPublished")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("PublishedResearch")}
                </button>
              </>
            )}

            {isExternalRequestGroupActive && (
              <>
                <button
                  onClick={() => handleTabChange(t("ExternalRequest"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ml-4 ${
                    activeTab === t("ExternalRequest") || activeTab === t("LegalResearchRequest")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("ExternalRequestList")}
                </button>

                <button
                  onClick={() => handleTabChange(t("ExternalNewRequest"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("ExternalNewRequest")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("NewRequest")}
                </button>

                <button
                  onClick={() => handleTabChange(t("ExternalScheduledPresentation"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("ExternalScheduledPresentation")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("ScheduledPresentation")}
                </button>

                <button
                  onClick={() => handleTabChange(t("ExternalPublished"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("ExternalPublished")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("PublishedResearch")}
                </button>
              </>
            )}

            {isCommitteeRequestGroupActive && (
              <>
                <button
                  onClick={() => handleTabChange(t("CommitteeRequest"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ml-4 ${
                    activeTab === t("CommitteeRequest")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("CommitteeRequestList")}
                </button>

                <button
                  onClick={() => handleTabChange(t("CommitteeNewRequest"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("CommitteeNewRequest")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("NewRequest")}
                </button>

                <button
                  onClick={() => handleTabChange(t("CommitteeScheduledPresentation"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("CommitteeScheduledPresentation")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("ScheduledPresentation")}
                </button>

                <button
                  onClick={() => handleTabChange(t("CommitteePublished"))}
                  type="button"
                  className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                    activeTab === t("CommitteePublished")
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  {t("PublishedResearch")}
                </button>
              </>
            )}
          </div>

          <div className="flex justify-between items-center gap-4 mr-7">
            {contextData.user.user_type == "external" && (
              <>
                <button
                  onClick={() => handleCreateRequest("request")}
                  type="button"
                  className="px-3 py-1.5 text-sm h-auto bg-black text-white rounded"
                >
                  {t("CreateNewRequest")}
                </button>
                <button
                  onClick={() => handleCreateRequest("committe-request")}
                  type="button"
                  className="px-3 py-1.5 text-sm h-auto bg-black text-white rounded"
                >
                  {t("CreateCommitteeRequest")}
                </button>
              </>
            )}

            {(contextData?.user?.roles?.some(
              (p) => p === "LegalResearchCoordinator"
            ) ||
              contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector"
              )) && (
              <button
                onClick={() => handleCreateRequest("internalRequest")}
                type="button"
                className="px-3 py-1.5 text-sm h-auto bg-black text-white rounded"
              >
                {t("CreateNewRequest")}
              </button>
            )}
          </div>
        </div>
      </div>

      {tabFilteredData.isLoading ? (
        <div className="flex justify-center items-center h-64">
          {LoadingPageIcon}
        </div>
      ) : (
        <DataTable
          title={`${activeTab} ${t("Table")}`}
          columns={columns}
          data={tabFilteredData.data}
          rowKey={(row) => row.uu_id}
          onSearch={(q) => {}}
          searchPlaceholder={t("SearchByApplicationNumber")}
          pagination={true}
        />
      )}
    </div>
  );
};

export default ConductLegalResearch;